/**
 * Module Runtime — Pure Logic Kernel
 *
 * Namespace utilities, dependency resolution, and content helpers.
 * No framework dependencies — all pure functions.
 */
/** Build namespace prefix from module id */
export function moduleNamespace(moduleId) {
    return `mod:${moduleId}`;
}
/** Prefix an ID with the module namespace */
export function namespacedId(moduleId, localId) {
    return `${moduleNamespace(moduleId)}/${localId}`;
}
/** Check if an ID belongs to a module */
export function isModuleContent(id) {
    return id.startsWith('mod:');
}
/** Extract module ID from a namespaced ID */
export function extractModuleId(nsId) {
    if (!isModuleContent(nsId))
        return null;
    const slash = nsId.indexOf('/');
    if (slash === -1)
        return null;
    return nsId.slice(4, slash); // skip "mod:"
}
/** Extract local ID (strip namespace) */
export function stripNamespace(id) {
    if (!isModuleContent(id))
        return id;
    const slash = id.indexOf('/');
    return slash === -1 ? id : id.slice(slash + 1);
}
/** Resolve module dependencies against available modules. Returns missing deps. */
export function resolveModuleDependencies(mod, available) {
    const requires = mod.requires;
    if (!requires || requires.length === 0)
        return { resolved: true, missing: [] };
    const missing = [];
    for (const dep of requires) {
        if (!available.has(dep.moduleId)) {
            missing.push(dep.moduleId);
        }
        // TODO: semver range checking in Phase 3
    }
    return { resolved: missing.length === 0, missing };
}
/** Check if a module targets the given game system */
export function isCompatibleWithSystem(mod, systemId) {
    if (!mod.system || mod.system === 'system-agnostic')
        return true;
    return mod.system === systemId;
}
// =============================================================================
// SYSTEM EXTENSION — Rule Composition
// =============================================================================
/**
 * Compose child system rules on top of a parent system.
 *
 * Merge strategy:
 *   - Scalars (name, shortName, license, publisher, abilityModFormula): child wins
 *   - Arrays (abilities, classes, conditions): child replaces entirely if present,
 *     otherwise inherits parent
 *   - Objects (characterTemplate, coreResolution, combat, magic, encounterBalance):
 *     shallow merge — child fields override parent fields
 *   - dmGuidance: shallow merge per field, commonMistakes concatenated
 */
export function composeSystemRules(parent, child) {
    return {
        // Scalars — child overrides
        name: child.name ?? parent.name,
        shortName: child.shortName ?? parent.shortName,
        license: child.license ?? parent.license,
        publisher: child.publisher ?? parent.publisher,
        // Ability framework — child replaces array if non-empty
        abilities: child.abilities && child.abilities.length > 0
            ? child.abilities
            : parent.abilities,
        abilityModFormula: child.abilityModFormula ?? parent.abilityModFormula,
        // Character template — shallow merge
        characterTemplate: {
            ...parent.characterTemplate,
            ...child.characterTemplate,
        },
        // Classes — child replaces if present, else inherit
        classes: child.classes ?? parent.classes,
        // Core resolution — shallow merge
        coreResolution: {
            ...parent.coreResolution,
            ...child.coreResolution,
        },
        // Combat — shallow merge if both exist, else prefer child then parent
        combat: child.combat && parent.combat
            ? { ...parent.combat, ...child.combat }
            : child.combat ?? parent.combat,
        // Conditions — child replaces if present
        conditions: child.conditions ?? parent.conditions,
        // Magic — shallow merge if both exist
        magic: child.magic && parent.magic
            ? { ...parent.magic, ...child.magic }
            : child.magic ?? parent.magic,
        // Encounter balance — shallow merge if both exist
        encounterBalance: child.encounterBalance && parent.encounterBalance
            ? { ...parent.encounterBalance, ...child.encounterBalance }
            : child.encounterBalance ?? parent.encounterBalance,
        // DM guidance — merge fields, concatenate commonMistakes
        dmGuidance: composeDmGuidance(parent.dmGuidance, child.dmGuidance),
    };
}
function composeDmGuidance(parent, child) {
    if (!parent && !child)
        return undefined;
    if (!parent)
        return child;
    if (!child)
        return parent;
    return {
        systemBrief: child.systemBrief || parent.systemBrief,
        combatNorms: child.combatNorms || parent.combatNorms,
        socialNorms: child.socialNorms || parent.socialNorms,
        toneGuidance: child.toneGuidance || parent.toneGuidance,
        commonMistakes: [
            ...parent.commonMistakes,
            ...child.commonMistakes.filter((m) => !parent.commonMistakes.includes(m)),
        ],
    };
}
/** Extract DM guidance text from a module */
export function extractDmGuidance(mod) {
    if (mod.rules?.dmGuidance)
        return mod.rules.dmGuidance.systemBrief;
    // Check if there's guidance in sessions
    if (Array.isArray(mod.sessions) &&
        mod.sessions.length > 0 &&
        typeof mod.sessions[0] === 'object' &&
        mod.sessions[0] !== null &&
        'dmNotes' in mod.sessions[0] &&
        typeof mod.sessions[0].dmNotes === 'string') {
        return mod.sessions[0].dmNotes;
    }
    return null;
}
//# sourceMappingURL=module-runtime.js.map